import React, { useEffect, useState } from 'react';
import { useLive } from '../context/LiveContext';

const GlobeSection = () => {
  const [rotation, setRotation] = useState(0);
  const { featuredScraper, featuredLocation, scrapers, SITE_TO_CITY } = useLive();

  useEffect(() => {
    const rotInt = setInterval(() => setRotation((r) => (r + 0.1) % 360), 50);
    return () => clearInterval(rotInt);
  }, []);

  // Build list of ping locations from currently running scrapers
  const activeLocations = scrapers
    .filter((s) => s.status === 'running')
    .map((s) => ({ ...SITE_TO_CITY[s.site], site: s.site, color: s.color }))
    .filter((l) => l.x !== undefined);

  return (
    <div className="relative w-full rounded-2xl border border-white/5 bg-gradient-to-b from-[#0a1220] to-[#050810] overflow-hidden">
      <div className="absolute top-4 left-5 z-20">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-mono">Europe · Cible</p>
      </div>
      <div className="absolute top-4 right-5 z-20 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-400" />
        </span>
        <span className="text-[10px] font-mono text-pink-200 uppercase tracking-widest">Live · {featuredScraper?.site}</span>
      </div>

      {/* Star field */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 60 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/70"
            style={{
              width: Math.random() * 2 + 0.5 + 'px',
              height: Math.random() * 2 + 0.5 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              opacity: Math.random() * 0.7 + 0.2,
              animation: `twinkle ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative flex items-center justify-center py-10 md:py-14">
        <div className="relative" style={{ width: 'min(85%, 480px)', aspectRatio: '1' }}>
          <div className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 120px 20px rgba(34,211,238,0.15), inset 0 0 60px rgba(34,211,238,0.08)' }} />

          <div
            className="absolute inset-0 rounded-full overflow-hidden"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200')",
              backgroundSize: '200% 100%',
              backgroundPosition: `${rotation}% center`,
              boxShadow: 'inset -20px -20px 60px rgba(0,0,0,0.7), inset 30px 20px 90px rgba(59,130,246,0.15)',
              filter: 'brightness(1.05) saturate(1.1)',
            }}
          />

          <div className="absolute -inset-2 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle at center, transparent 62%, rgba(34,211,238,0.25) 68%, transparent 74%)' }} />

          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <clipPath id="globeClip"><circle cx="50" cy="50" r="49" /></clipPath>
            </defs>
            <g clipPath="url(#globeClip)" stroke="rgba(34,211,238,0.12)" strokeWidth="0.15" fill="none">
              {[20, 35, 50, 65, 80].map((y) => (
                <ellipse key={y} cx="50" cy={y} rx="49" ry="2" />
              ))}
              {[10, 25, 50, 75, 90].map((x) => (
                <ellipse key={x} cx={x} cy="50" rx="3" ry="49" />
              ))}
            </g>
          </svg>

          {/* Draw all active scraper locations */}
          {activeLocations.map((loc) => {
            const isFeatured = loc.site === featuredScraper?.site;
            const color = isFeatured ? '#ec4899' : loc.color || '#22d3ee';
            return (
              <div
                key={loc.site}
                className="absolute"
                style={{ left: `${loc.x}%`, top: `${loc.y}%`, transform: 'translate(-50%, -50%)' }}
              >
                <div className={`relative ${isFeatured ? 'z-10' : ''}`}>
                  {isFeatured && (
                    <>
                      <span className="absolute rounded-full" style={{ animation: 'ping-slow 2s cubic-bezier(0,0,0.2,1) infinite', background: `${color}55`, width: '32px', height: '32px', left: '-16px', top: '-16px' }} />
                      <span className="absolute rounded-full" style={{ animation: 'ping-slow 2s cubic-bezier(0,0,0.2,1) infinite 0.5s', background: `${color}44`, width: '32px', height: '32px', left: '-16px', top: '-16px' }} />
                    </>
                  )}
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ background: color, boxShadow: isFeatured ? `0 0 12px 3px ${color}` : `0 0 6px ${color}` }}
                  />
                  {isFeatured && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 whitespace-nowrap">
                      <div className="px-2 py-0.5 rounded bg-black/70 border border-pink-500/40 text-[10px] font-mono text-pink-200 uppercase tracking-wider">
                        {featuredLocation.city} · {loc.site}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Sweeping radar line */}
          <div className="absolute inset-0 rounded-full pointer-events-none overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'conic-gradient(from 0deg, transparent 0deg, rgba(34,211,238,0.25) 30deg, transparent 60deg)',
                animation: 'spin-slow 6s linear infinite',
                borderRadius: '50%',
                mixBlendMode: 'screen',
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom info bar - dynamic */}
      <div className="relative border-t border-white/5 bg-black/30 px-6 py-3 grid grid-cols-4 gap-4">
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-mono">Site actif</p>
          <p className="font-mono text-white mt-0.5 text-sm">{featuredScraper?.site || '—'}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-mono">Ville</p>
          <p className="font-mono text-white mt-0.5 text-sm">{featuredLocation?.city || '—'}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-mono">Pays</p>
          <p className="font-mono text-white mt-0.5 text-sm">{featuredLocation?.country || '—'}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-mono">Avancement</p>
          <p className="font-mono text-cyan-300 mt-0.5 text-sm tabular-nums">{Math.round(featuredScraper?.progress || 0)}%</p>
        </div>
      </div>
    </div>
  );
};

export default GlobeSection;
